import { deepmerge } from '@mui/utils';

/* The order of these files is important, so probably don't change it unless you're sure about what you're doing */
import '@styles/reset.css';
import { createTheme } from '@mui/material/styles';
import '@styles/colors.config.css';
import '@styles/type.config.css';
import '@styles/global.config.css';
import '@styles/global.css';

import { getDOKThemeColors } from '@aware/common/dok-filter/constants';
import PALETTES from './color-palette';

const URL_GOOGLE_ROBOTO =
  'https://fonts.googleapis.com/css2?family=Roboto+Mono:wght@400;700&family=Roboto:wght@400;500;700&display=swap';
const URL_GOOGLE_MATERIAL_ICONS = 'https://fonts.googleapis.com/icon?family=Material+Icons';

const addLinkElement = href => {
  const linkElement = document.createElement('link'); // create an <link> tag
  linkElement.rel = 'stylesheet';
  linkElement.href = href;
  document.getElementsByTagName('head')[0].appendChild(linkElement);
};

addLinkElement(URL_GOOGLE_MATERIAL_ICONS);
addLinkElement(URL_GOOGLE_ROBOTO);

const APP_PREFIX = window.location.pathname.split('/')[1]; // awarev2, strive, etc.

/**
 * Creating base theme so that I can use default theme options when building other themes,
 * e.g. theme.palette.dividerColor
 *
 * See "Theme composition: using theme options to define other options" heading
 * at https://mui.com/material-ui/customization/theming/#api
 */
const muiDefaultTheme = createTheme({
  typography: {
    fontFamily: ['Roboto', 'Ubuntu', 'Sans-serif'].join(','),
    data: {
      fontFamily: ['Roboto Mono', 'Ubuntu Mono', 'Monospace'].join(',')
    }
  }
});

// Destructure common properties from default MUI typography object for DRY code
const {
  pxToRem,
  fontWeightBold: BOLD,
  fontWeightMedium: MEDIUM,
  fontWeightRegular: REGULAR,
  fontWeightLight: LIGHT,
  data: { fontFamily: DATA_FONT }
} = muiDefaultTheme.typography;
const { grey: GREY } = PALETTES;

// Create new typography variants based on updated system design
const typographyVariants = {
  pageTitle: {
    fontSize: pxToRem(22),
    fontWeight: MEDIUM,
    letterSpacing: pxToRem(0.5),
    lineHeight: pxToRem(26),
    color: GREY[950]
  },
  pageSection: {
    fontSize: pxToRem(20),
    fontWeight: MEDIUM,
    letterSpacing: pxToRem(0.5),
    lineHeight: pxToRem(24)
  },
  containerTitle: {
    fontSize: pxToRem(18),
    fontWeight: MEDIUM,
    letterSpacing: pxToRem(0.25),
    lineHeight: pxToRem(22)
  },
  containerSection: {
    fontSize: pxToRem(16),
    fontWeight: MEDIUM,
    letterSpacing: pxToRem(0.25),
    lineHeight: pxToRem(20)
  },
  overline: {
    fontSize: pxToRem(12),
    fontWeight: REGULAR,
    letterSpacing: pxToRem(1),
    lineHeight: pxToRem(16),
    textTransform: 'uppercase'
  },
  caption: {
    fontSize: pxToRem(12),
    fontWeight: REGULAR,
    lineHeight: pxToRem(16)
  },
  subtitle1: {
    fontSize: pxToRem(14),
    fontWeight: BOLD,
    letterSpacing: pxToRem(0.5),
    lineHeight: pxToRem(18)
  }
};

/*
The following variants are also available as defaults from the MUI object.

subtitle 2 {
    "fontFamily": "Roboto,Ubuntu,Sans-serif",
    "fontWeight": 500,
    "fontSize": "0.875rem",
    "lineHeight": 1.57
}
body 1 {
    "fontFamily": "Roboto,Ubuntu,Sans-serif",
    "fontWeight": 400,
    "fontSize": "1rem",
    "lineHeight": 1.5
}
body2 {
    "fontFamily": "Roboto,Ubuntu,Sans-serif",
    "fontWeight": 400,
    "fontSize": "0.875rem",
    "lineHeight": 1.43
}
*/

// Create new data visualization typography variants based on updated system design
const dataVisualizationVariants = {
  dataLarge: {
    fontFamily: DATA_FONT,
    fontSize: '3.75rem',
    fontWeight: LIGHT,
    lineHeight: 1.2
  },
  dataMedium: {
    fontFamily: DATA_FONT,
    fontSize: '2.375rem',
    fontWeight: REGULAR,
    lineHeight: 1.235
  },
  dataContent: {
    ...muiDefaultTheme.typography.data,
    fontSize: pxToRem(20),
    lineHeight: pxToRem(24),
    fontFamily: DATA_FONT
  },
  dataTableContent: {
    ...muiDefaultTheme.typography.body2,
    fontSize: muiDefaultTheme.typography.caption.fontSize,
    fontFamily: DATA_FONT
  }
};

// These are theme properties that are common across any theme we build
const commonThemeProperties = {
  palette: {
    default: {
      main: '#333'
    },
    tableRowAltColor: GREY[50], // For zebra striping
    action: {
      selectedOpacity: '0.3',
      hoverOpacity: '0.05'
    },
    ...PALETTES,
    ...getDOKThemeColors(PALETTES)
  },
  typography: {
    ...muiDefaultTheme.typography,
    ...typographyVariants,
    ...dataVisualizationVariants,
    tableHeading: {
      ...muiDefaultTheme.typography.overline,
      lineHeight: muiDefaultTheme.spacing(1.75)
    }
  },
  mixins: {
    denseToolbar: {
      minHeight: 48
    },
    navigationDrawer: {
      openWidth: 280,
      closedWidth: 64
    }
  },
  components: {
    MuiAccordionSummary: {
      variants: [
        {
          props: { variant: 'containerSection' },
          style: typographyVariants.containerSection
        },
        {
          props: { variant: 'containerTitle' },
          style: typographyVariants.containerTitle
        }
      ]
    },
    MuiButton: {
      defaultProps: {
        color: 'default'
      },
      styleOverrides: { root: { textTransform: 'initial', fontSize: '.925rem' } },
      variants: [
        {
          props: { variant: 'contained', color: 'default' },
          style: { backgroundColor: '#E0E0E0' }
        }
      ]
    },
    MuiIconButton: {
      variants: [
        {
          props: { color: 'default' },
          style: { color: GREY[600] }
        }
      ]
    },
    MuiCardHeader: {
      styleOverrides: {
        title: typographyVariants.containerTitle,
        content: typographyVariants.containerTitle,
        subheader: typographyVariants.subtitle1
      }
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          padding: muiDefaultTheme.spacing(0.875, 1.125)
        }
      }
    },
    MuiFilledInput: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(0, 0, 0, 0.05)',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.09)'
          }
        }
      }
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          padding: muiDefaultTheme.spacing(0.875, 1.125)
        }
      }
    },
    MuiTab: {
      styleOverrides: {
        root: { textTransform: 'initial', padding: 8, minHeight: 40 }
      }
    },
    MuiTabs: {
      styleOverrides: {
        root: { minHeight: 40 }
      }
    },
    MuiTableCell: {
      variants: [
        {
          props: { variant: 'data' },
          style: {
            fontFamily: DATA_FONT
          }
        }
      ]
    },
    MuiTooltip: {
      defaultProps: { arrow: true, enterDelay: 500 },
      styleOverrides: {
        tooltip: {
          fontSize: '0.8rem'
        }
      }
    },
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          pageTitle: 'h1',
          pageSection: 'h2',
          containerTitle: 'h3',
          containerSection: 'h4'
        },
        variant: 'body2'
      }
    }
  }
};

// Create original theme by merging commonThemeProperties and custom theme properties
const theme = createTheme(
  deepmerge(commonThemeProperties, {
    palette: {
      primary: {
        main: '#2864c7',
        background: '#2864c720'
      },
      secondary: {
        main: { strive: '#EAB63B', beacon: '#2E72E1' }[APP_PREFIX] || '#E0643A',
        dark: { strive: '#aa8328', beacon: '#1A4080' }[APP_PREFIX] || '#b8512e',
        background: { strive: '#EAB63B20', beacon: '#E8F0FC' }[APP_PREFIX] || '#E0643A20'
      },
      error: {
        main: '#B00020'
      },
      success: {
        contrastText: '#FFF',
        main: '#54793E'
      },
      warning: {
        contrastText: '#FFF',
        main: '#CE8D28'
      },
      info: {
        contrastText: '#FFF',
        main: GREY[700]
      },
      common: {
        success: '#54793E',
        error: '#B00020'
      },
      appBackground: {
        main: '#FCFCFB'
      },

      onlineTestingDefaults: {
        main: '#2871bd',
        alternate: '#128843',
        background: {
          main: '#f6f6f6',
          alternate: '#e8e8e8'
        },
        selected: {
          text: '#ffffff',
          border: '#000000'
        }
      },
      pageTitle: '#33ff00',
      navigationDrawer: {
        background: {
          base: '#FFF',
          hover: GREY[200],
          selected: { strive: '#FCF6E8', beacon: '#E8F0FC' }[APP_PREFIX] || '#FCEDE8'
        },
        icon: {
          base: GREY[600],
          hover: GREY[600],
          selected: { strive: '#806021', beacon: '#1A4080' }[APP_PREFIX] || '#803921'
        },
        text: {
          base: GREY[700],
          hover: '#000',
          selected: '#000'
        },
        dividerColor: muiDefaultTheme.palette.dividerColor
      },
      // DataGrid
      tableAction: {
        hoverColor: '#ECF3FF',
        selectedColor: '#DFE9FA'
      }
    }
  })
);

delete theme.typography.h1;
delete theme.typography.h2;
delete theme.typography.h3;
delete theme.typography.h4;
delete theme.typography.h5;
delete theme.typography.h6;
export default theme;

// Create studentPortal theme by merging commonThemeProperties and custom theme properties
const STUDENT_PORTAL_PRIMARY_MAIN = '#4F8EF5';
export const studentPortalTheme = createTheme(
  deepmerge(theme, {
    palette: {
      pageTitle: STUDENT_PORTAL_PRIMARY_MAIN,
      navigationDrawer: {
        background: {
          selected: '#e8f0fc'
        },
        icon: {
          selected: '#194080'
        }
      }
    }
  })
);
